import { useState, useEffect, useRef } from "react";
import * as signalR from "@microsoft/signalr";
import SupportCaseAPI from "~/api/supportCaseApi";
import { toast } from "~/components/HeaderNotification";

const apiUrl = process.env.REACT_APP_API_URL;

export default function useSupportCaseAdmin() {
    const [activeTab, setActiveTab] = useState("PendingTab");
    const [pendingCase, setPendingCase] = useState([]);
    const [chattingCase, setChattingCase] = useState([]);
    const [messageGroup, setMessageGroup] = useState([]);
    const [selectedChat, setSelectedChat] = useState({});
    const [reloadTickets, setReloadTickets] = useState(false);
    const [messageInput, setMessageInput] = useState("");
    const [allowChat, setAllowChat] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [customerTyping, setCustomerTyping] = useState(false);

    const [connection, setConnection] = useState(null);
    const [activeUser, setActiveUser] = useState(null);

    const [isFocused, setIsFocused] = useState(false);
    const [hasUnread, setHasUnread] = useState(false);

    const chatEndRef = useRef(null);
    const [toggleScrollEndChat, setToggleScrollEndChat] = useState(false);

    const [showCaseFileModal, setShowCaseFileModal] = useState(false);
    const [caseFiles, setCaseFiles] = useState([]);

    const selectedChatRef = useRef(selectedChat);

    useEffect(() => {
        selectedChatRef.current = selectedChat;
    }, [selectedChat]);

    // Ensure we join the ticket room if connection connects after chat is selected, or on reconnect
    useEffect(() => {
        if (connection && selectedChat?.TicketId) {
            connection.invoke("JoinTicketRoom", selectedChat.TicketId).catch(() => { });
        }
    }, [connection, selectedChat?.TicketId]);

    // Fetch Tickets
    const fetchTickets = async () => {
        try {
            const res = await SupportCaseAPI.getAdminTickets();
            const tickets = res.data?.Results || res.data?.results || res.data?.Data?.Results || res.data?.data?.results || [];

            // Map the history model to match UI requirements
            const mappedTickets = tickets.map(t => ({
                ...t,
                TicketId: t.TicketId || t.ticketId,
                CaseSessionId: t.TicketId || t.ticketId,
                SupportCaseId: t.TicketId || t.ticketId,
                CaseType: t.Subject || t.subject,
                CreatedDate: t.CreatedAt || t.createdAt,
                CountCustomerChatUnread: 0, // In this new system, we rely on TicketNewMessage event to update this locally if needed
            }));

            setPendingCase(mappedTickets.filter(t => (t.Status || t.status)?.toLowerCase() === "waiting"));
            setChattingCase(mappedTickets.filter(t => (t.Status || t.status)?.toLowerCase() === "active"));
        } catch (error) {
            console.error(error);
        }
    };

    useEffect(() => {
        fetchTickets();
    }, [reloadTickets]);

    const isFocusedRef = useRef(isFocused);
    useEffect(() => {
        isFocusedRef.current = isFocused;
    }, [isFocused]);

    // SignalR Connection
    useEffect(() => {
        const connect = new signalR.HubConnectionBuilder()
            .withUrl(`${apiUrl}/support-hub`, {
                withCredentials: true
            })
            .withAutomaticReconnect()
            .build();

        let isMounted = true;

        connect.start()
            .then(() => {
                if (isMounted) {
                    setConnection(connect);
                } else {
                    connect.stop();
                }
            })
            .catch(() => { });

        // New Ticket arriving
        connect.on("NewTicket", (data) => {
            setReloadTickets(prev => !prev);
        });

        connect.on("ReceiveMessage", async (data) => {
            const chat = selectedChatRef.current;

            if (String(chat?.TicketId) === String(data.ticketId)) {
                // Determine if sender is admin or customer
                const isSupporter = data.senderRole === "admin";

                // Prevent duplicate if it's the admin's own message (we already added it optimistically)
                if (!isSupporter) {
                    handleMessageChange(isSupporter, data.message, "", data.createdAt, data.IsRead, data.imageUrl);
                    setToggleScrollEndChat(prev => !prev);
                }

                if (document.hidden) {
                    setHasUnread(true);
                    return;
                }

                // If chat is active and focused, and message is from student, mark as read
                if (isFocusedRef.current && data.senderRole === "customer") {
                    await connect.invoke("MarkAsRead", data.ticketId).catch(() => { });
                } else {
                    setHasUnread(true);
                }

                if (data.senderRole === "customer") {
                    setChattingCase(prevCases => prevCases.map(item =>
                        String(item.TicketId) === String(data.ticketId) ? { ...item, LastChatCustomer: data.message } : item
                    ));
                    setPendingCase(prevCases => prevCases.map(item =>
                        String(item.TicketId) === String(data.ticketId) ? { ...item, LastChatCustomer: data.message } : item
                    ));
                }
            }
        });

        connect.on("TicketNewMessage", (data) => {
            // Increase unread count if we are not actively in this chat
            const chat = selectedChatRef.current;
            if (String(chat?.TicketId) !== String(data.ticketId)) {
                setChattingCase(prevCases =>
                    prevCases.map(item => {
                        if (String(item.TicketId) === String(data.ticketId)) {
                            return {
                                ...item,
                                LastChatCustomer: data.message,
                                CountCustomerChatUnread: (item.CountCustomerChatUnread || 0) + 1
                            };
                        }
                        return item;
                    })
                );
                setPendingCase(prevCases =>
                    prevCases.map(item => {
                        if (String(item.TicketId) === String(data.ticketId)) {
                            return {
                                ...item,
                                LastChatCustomer: data.message,
                                CountCustomerChatUnread: (item.CountCustomerChatUnread || 0) + 1
                            };
                        }
                        return item;
                    })
                );
            }
        });

        let typingTimeout;
        connect.on("UserTyping", (data) => {
            const chat = selectedChatRef.current;
            if (String(chat?.TicketId) === String(data.ticketId) && data.role === "customer") {
                setCustomerTyping(data.isTyping);
                if (typingTimeout) clearTimeout(typingTimeout);
                if (data.isTyping) {
                    typingTimeout = setTimeout(() => {
                        setCustomerTyping(false);
                    }, 3000);
                }
            }
        });

        connect.on("TicketStatusChanged", (data) => {
            setReloadTickets(prev => !prev);
        });

        connect.on("TicketClosed", (data) => {
            const chat = selectedChatRef.current;
            if (String(chat?.TicketId) === String(data.ticketId)) {
                setSelectedChat({});
                setMessageGroup([]);
            }
            setReloadTickets(prev => !prev);
        });

        connect.on("MessagesRead", (data) => {
            if (data.role === "customer") {
                setMessageGroup(prev => {
                    if (prev.length === 0) return prev;
                    const newK = [...prev];
                    const lastIndex = newK.length - 1;
                    const updatedGroup = newK[lastIndex].map(item =>
                        item.IsRead === false ? { ...item, IsRead: true, Status: "sent" } : item
                    );
                    newK[lastIndex] = updatedGroup;
                    return newK;
                });
            }
        });

        return () => {
            isMounted = false;
            if (connect.state !== signalR.HubConnectionState.Disconnected && connect.state !== signalR.HubConnectionState.Connecting) {
                connect.stop();
            }
        };
    }, [activeUser]);

    // Handle Unread on Focus
    useEffect(() => {
        if (isFocused && hasUnread && Object.keys(selectedChat).length > 0 && connection) {
            connection.invoke("MarkAsRead", selectedChat.TicketId).catch(() => { });
            setHasUnread(false);
        }
    }, [isFocused, hasUnread, selectedChat, connection]);

    // Scroll to End
    useEffect(() => {
        chatEndRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
    }, [toggleScrollEndChat]);

    const groupMessagesBySenderSequence = (messages) => {
        const grouped = [];
        let currentGroup = [];

        messages.forEach((msg) => {
            // map new message schema to old format
            const mappedMsg = {
                ...msg,
                SupporterOrCustomer: msg.senderRole === "admin" ? "Supporter" : "Customer",
                ChatContent: msg.message,
                CreatedDate: msg.createdAt,
                Status: "sent",
                IsRead: msg.isRead || true, // assuming old messages are read
                ImageUrl: msg.imageUrl
            };

            if (
                currentGroup.length === 0 ||
                currentGroup[currentGroup.length - 1].SupporterOrCustomer === mappedMsg.SupporterOrCustomer
            ) {
                currentGroup.push(mappedMsg);
            } else {
                grouped.push(currentGroup);
                currentGroup = [mappedMsg];
            }
        });

        if (currentGroup.length > 0) grouped.push(currentGroup);
        return grouped;
    };

    const handleOnClickChattingCase = async (id) => {
        setIsFocused(true);
        const targetChat = chattingCase.find((x) => x.TicketId === id);
        setSelectedChat(prev => targetChat || prev);
        setAllowChat(true);

        if (selectedChat.TicketId === id && allowChat) return;

        try {
            const res = await SupportCaseAPI.getTicketMessages(id);
            // Robust extraction
            let messages = [];
            if (Array.isArray(res.data)) messages = res.data;
            else if (Array.isArray(res.data?.messages)) messages = res.data.messages;
            else if (Array.isArray(res.data?.data?.messages)) messages = res.data.data.messages;
            else if (Array.isArray(res.data?.Data?.messages)) messages = res.data.Data.messages;

            setMessageGroup(groupMessagesBySenderSequence(messages));

            // clear unread locally
            setChattingCase(prevCases =>
                prevCases.map(item =>
                    item.TicketId === id
                        ? { ...item, CountCustomerChatUnread: 0 }
                        : item
                )
            );

            if (targetChat?.CountCustomerChatUnread > 0) {
                setHasUnread(true);
            }

            if (connection) {
                connection.invoke("JoinTicketRoom", id).catch(() => { });
            }
        } catch (error) {
            console.error(error);
        }
    };

    const handleOnClickPendingCase = async (item) => {
        setIsFocused(true);
        setSelectedChat(item);
        setAllowChat(false);
        try {
            const res = await SupportCaseAPI.getTicketMessages(item.TicketId);

            // Robust extraction
            let messages = [];
            if (Array.isArray(res.data)) messages = res.data;
            else if (Array.isArray(res.data?.messages)) messages = res.data.messages;
            else if (Array.isArray(res.data?.data?.messages)) messages = res.data.data.messages;
            else if (Array.isArray(res.data?.Data?.messages)) messages = res.data.Data.messages;

            setMessageGroup(groupMessagesBySenderSequence(messages));
            if (connection) {
                connection.invoke("JoinTicketRoom", item.TicketId).catch(() => { });
            }
        } catch (error) {
            console.error(error);
            setMessageGroup([]);
        }
    };

    const handleMessageChange = (isSupporter, chatContent, tempId = "", createdAt = null, isRead = false, imageUrl = null) => {
        const currentRole = isSupporter ? "Supporter" : "Customer";
        const newMessage = {
            SupporterOrCustomer: currentRole,
            ChatContent: chatContent,
            CreatedDate: createdAt || new Date().toISOString(),
            TempId: tempId,
            Status: isSupporter ? "sending" : "",
            IsRead: isRead,
            ImageUrl: imageUrl
        };

        setMessageGroup(prevMessages => {
            const updatedMessages = [...prevMessages];
            const lastGroup = updatedMessages[updatedMessages.length - 1];
            if (lastGroup && (lastGroup[0].SupporterOrCustomer === currentRole)) {
                updatedMessages[updatedMessages.length - 1] = [...lastGroup, newMessage];
            } else {
                updatedMessages.push([{ ...newMessage }]);
            }
            return updatedMessages;
        });
    };

    const handleSendMessage = async () => {
        if (!messageInput.trim()) return;
        const currentInput = messageInput;
        setMessageInput("");
        const tempId = "temp_" + Date.now();
        handleMessageChange(true, currentInput, tempId);

        if (connection) {
            try {
                await connection.invoke("SendMessage", selectedChat.TicketId, currentInput, null);

                // Update local status to sent (simulated for immediate feedback)
                setMessageGroup(prev =>
                    prev.map(group => {
                        let changed = false;
                        const updatedGroup = group.map(item => {
                            if (item.TempId === tempId) {
                                changed = true;
                                return { ...item, Status: "sent" };
                            }
                            return item;
                        });
                        return changed ? updatedGroup : group;
                    })
                );
            } catch (error) {
                // Update local status to failed
                setMessageGroup(prev =>
                    prev.map(group => {
                        let changed = false;
                        const updatedGroup = group.map(item => {
                            if (item.TempId === tempId) {
                                changed = true;
                                return { ...item, Status: "failed" };
                            }
                            return item;
                        });
                        return changed ? updatedGroup : group;
                    })
                );
            }
        }
        setToggleScrollEndChat(prev => !prev);
    };

    const handleKeyDown = async (e) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            if (!messageInput.trim()) return;

            // Stop typing indicator when sending
            handleStopTyping();

            if (!allowChat) return;
            handleSendMessage();
        }
    };

    const handleOnClickAccept = async (id) => {
        if (connection) {
            try {
                await connection.invoke("AcceptTicket", id);
                toast.success(<span className="fs-9 fw-bold">Accepted support ticket successfully.</span>);

                // Update selectedChat status immediately so UI reflects active state
                setSelectedChat(prev => ({ ...prev, Status: "active" }));
                setAllowChat(true);
                setReloadTickets(prev => !prev);
                setActiveTab("ChatTab");

                // Automatically open it in the chat window
                handleOnClickChattingCase(id);
            } catch (err) {
                toast.error(<span className="fs-9 fw-bold">An error occurred while accepting support ticket.</span>);
            }
        }
    };

    const handleCloseChat = async () => {
        if (connection) {
            try {
                await connection.invoke("CloseTicket", selectedChat.TicketId);
                toast.success(<span className="fs-9 fw-bold">Resolved support ticket successfully.</span>);
                setSelectedChat({});
                setMessageGroup([]);
                setReloadTickets(prev => !prev);
            } catch {
                toast.error(<span className="fs-9 fw-bold">An error occurred while resolving support ticket.</span>);
            }
        }
    };

    const handleOpenCaseFile = () => {
        // Feature disabled or to be implemented via new Ticket logic
        toast.info("Feature currently disabled in the new system.");
    };

    const handleBackToList = () => {
        setSelectedChat({});
    };

    const rejectSupportCase = async (data, resetForm) => {
        // Backend does not support Reject explicitly, we just close it
        if (connection) {
            try {
                await connection.invoke("CloseTicket", data.SupportCaseId);
                toast.success(<span className="fs-9 fw-bold">Ticket rejected/closed successfully.</span>);
                setReloadTickets(prev => !prev);
                setSelectedChat({});
                setMessageGroup([]);
            } catch (err) {
                toast.error(<span className="fs-9 fw-bold">An error occurred while rejecting ticket.</span>);
            }
            setShowModal(false);
        }
    };

    const typingTimeoutRef = useRef(null);

    const handleStopTyping = () => {
        if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
        if (connection && selectedChat?.TicketId && connection.state === "Connected") {
            connection.invoke("Typing", selectedChat.TicketId, false).catch(() => { });
        }
    };

    const handleMessageInputChange = (val) => {
        setMessageInput(val);
        if (connection && selectedChat?.TicketId) {
            if (val.trim() === "") {
                handleStopTyping();
                return;
            }

            connection.invoke("Typing", selectedChat.TicketId, true).catch(() => { });

            if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
            typingTimeoutRef.current = setTimeout(() => {
                if (connection?.state === "Connected") {
                    connection.invoke("Typing", selectedChat.TicketId, false).catch(() => { });
                }
            }, 2000);
        }
    };

    return {
        activeTab,
        setActiveTab,
        pendingCase,
        chattingCase,
        messageGroup,
        selectedChat,
        messageInput,
        setMessageInput,
        handleMessageInputChange,
        handleStopTyping,
        customerTyping,
        showModal,
        setShowModal,
        setIsFocused,
        chatEndRef,
        showCaseFileModal,
        setShowCaseFileModal,
        caseFiles,
        handleOnClickChattingCase,
        handleOnClickPendingCase,
        handleKeyDown,
        handleOnClickAccept,
        handleCloseChat,
        handleOpenCaseFile,
        rejectSupportCase,
        handleBackToList
    };
}
